import React, { useEffect, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useHandHistory } from 'hooks/useHandHistory';

import { BoardCards } from './Cards';
import { formatAmount, formatHandTime, formatPot } from './format';
import { buildDisplayPotGroups } from './HandSnapshot';
import { canRevealOwnCards, getPotTotal } from './handSummary';
import { PlayByPlayList } from './PlayByPlayList';
import { PotBreakdown } from './PotBreakdown';
import { PotGroup } from './PotGroup';
import { TableSnapshot } from './TableSnapshot';
import {
  HandHistorySnapshot,
  PotGroupSnapshot,
  SeatSnapshot,
  TableSnapshot as TableSnapshotData
} from './types';

import styles from './HandHistoryV1.module.scss';

const buildDisplayTableSnapshot = (
  hand: HandHistorySnapshot,
  displayPotGroups: PotGroupSnapshot[]
): TableSnapshotData => {
  const seatsBySeat = new Map<number, SeatSnapshot>(
    hand.tableSnapshot.seats.map((seat) => [seat.seat, seat])
  );

  displayPotGroups.forEach((pot) => {
    pot.players.forEach((player) => {
      const seat = seatsBySeat.get(player.seat);

      seatsBySeat.set(player.seat, {
        ...seat,
        seat: player.seat,
        name: player.name,
        avatar: player.avatar ?? seat?.avatar,
        stack: seat?.stack,
        isDealer: player.isDealer ?? seat?.isDealer,
        isWinner: Boolean(seat?.isWinner || player.isWinner),
        folded: player.folded ?? seat?.folded,
        mucked: player.mucked ?? seat?.mucked,
        cards: player.cards ?? seat?.cards,
        cardsRevealed: player.cardsRevealed ?? seat?.cardsRevealed,
        net: seat?.net ?? player.net
      });
    });
  });

  const seats = Array.from(seatsBySeat.values()).sort((a, b) => a.seat - b.seat);

  return {
    ...hand.tableSnapshot,
    totalSeats: Math.max(hand.tableSnapshot.totalSeats ?? seats.length, seats.length),
    seats
  };
};

/** Snapshot-first body retained for the playground V1 comparison. */
export const HandHistoryV1Snapshot = ({ hand }: { hand: HandHistorySnapshot }) => {
  const { revealOwnCards } = useHandHistory();
  const potTotal = getPotTotal(hand);
  const [isPlayByPlayOpen, setIsPlayByPlayOpen] = useState(false);
  const [isSnapshotOpen, setIsSnapshotOpen] = useState(true);
  const displayPotGroups = useMemo(() => buildDisplayPotGroups(hand), [hand]);
  const displaySnapshot = useMemo(
    () => buildDisplayTableSnapshot(hand, displayPotGroups),
    [displayPotGroups, hand]
  );
  useEffect(() => {
    const viewerSeat = hand.tableSnapshot.viewerSeat;
    if (!canRevealOwnCards(viewerSeat, displaySnapshot.seats)) return;
    revealOwnCards(hand.gameNo, hand.completedAt);
  }, [
    displaySnapshot.seats,
    hand.completedAt,
    hand.gameNo,
    hand.tableSnapshot.viewerSeat,
    revealOwnCards
  ]);

  const avatarBySeat = useMemo(
    () =>
      [...displaySnapshot.seats]
        .sort((a, b) => a.seat - b.seat)
        .reduce<Record<number, string>>((avatars, seat, index) => {
          avatars[seat.seat] = seat.avatar ?? `${String(index + 1).padStart(3, '0')}-saloon.png`;
          return avatars;
        }, {}),
    [displaySnapshot.seats]
  );

  return (
    <div className={styles.handSnapshot}>
      <div className={styles.handSnapshotBody}>
        {hand.gameType === 'tournament' ? (
          <>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <span>Level</span>
                <strong>{hand.metadata.level ?? '-'}</strong>
              </div>
            </section>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <span>Blinds</span>
                <strong>
                  {hand.metadata.smallBlind ?? 0} / {hand.metadata.bigBlind ?? 0}
                  {hand.metadata.ante ? ` · Ante ${hand.metadata.ante}` : ''}
                </strong>
              </div>
            </section>
          </>
        ) : (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Blinds</span>
              <strong>
                {formatPot(hand.metadata.smallBlind ?? 0, hand.gameType)} /{' '}
                {formatPot(hand.metadata.bigBlind ?? 0, hand.gameType)}
              </strong>
            </div>
          </section>
        )}

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span>Board</span>
          </div>
          <div>
            {(hand.boards && hand.boards.length > 1 ? hand.boards : [hand.board]).map(
              (board, index) => (
                <div key={index}>
                  {hand.boards && hand.boards.length > 1 && <small>Run {index + 1}</small>}
                  <BoardCards cards={board} />
                </div>
              )
            )}
          </div>
        </section>

        <section className={styles.snapshotSection}>
          <button
            type='button'
            className={styles.snapshotToggle}
            aria-expanded={isSnapshotOpen}
            onClick={() => setIsSnapshotOpen((current) => !current)}>
            <span>Table Snapshot</span>
            <KeyboardArrowDownIcon className={isSnapshotOpen ? styles.toggleOpen : ''} />
          </button>
          {isSnapshotOpen && (
            <TableSnapshot
              snapshot={displaySnapshot}
              gameType={hand.gameType}
              gameVariant={hand.gameVariant}
              bettingStructure={hand.bettingStructure}
              metadata={hand.metadata}
              potTotal={potTotal}
              potGroups={hand.potGroups}
            />
          )}
        </section>

        {hand.viewerResult && (
          <section className={styles.handResultSummary}>
            <span className={styles.handResultLabel}>Hand Result</span>
            <div className={styles.handResultValue}>
              <span>
                {hand.viewerResult.outcome === 'won'
                  ? 'You won'
                  : hand.viewerResult.outcome === 'lost'
                  ? 'You lost'
                  : 'Break even'}
              </span>
              <strong>{formatAmount(hand.viewerResult.net, hand.gameType)}</strong>
            </div>
            <span className={styles.handResultMeta}>Net P&amp;L</span>
            {hand.viewerResult.scoop && <span className={styles.scoopBadge}>Scoop</span>}
          </section>
        )}

        {hand.pots?.length
          ? hand.pots.map((pot) => (
              <PotBreakdown
                key={pot.potId}
                pot={pot}
                gameType={hand.gameType}
                viewerSeat={hand.tableSnapshot.viewerSeat}
                avatarBySeat={avatarBySeat}
              />
            ))
          : displayPotGroups.map((pot) => (
              <PotGroup
                key={pot.name}
                pot={pot}
                gameType={hand.gameType}
                viewerSeat={hand.tableSnapshot.viewerSeat}
                avatarBySeat={avatarBySeat}
                showAmount={pot.name !== 'No Payout'}
                collapsible={pot.name === 'No Payout'}
              />
            ))}

        <section className={styles.playByPlaySection}>
          <button
            type='button'
            className={styles.playByPlayToggle}
            aria-expanded={isPlayByPlayOpen}
            onClick={() => setIsPlayByPlayOpen((current) => !current)}>
            <span>Play-by-play</span>
            <strong>{hand.playByPlay.length} events</strong>
            <KeyboardArrowDownIcon className={isPlayByPlayOpen ? styles.toggleOpen : ''} />
          </button>
          {isPlayByPlayOpen && (
            <PlayByPlayList events={hand.playByPlay} gameType={hand.gameType} showTableCards />
          )}
        </section>
      </div>

      <footer className={styles.handSnapshotFooter}>
        <span>Hand concluded</span>
        <strong>{formatHandTime(hand.completedAt)}</strong>
      </footer>
    </div>
  );
};
